/**
 * Behavior for snippets/swist-filter-bar.liquid.
 * One panel serves two modes: "single" (one filter as a popover/drop under its pill) and
 * "all" (every filter as accordions in a drawer/sheet). Changes update a live product count;
 * the apply button navigates to the filtered URL. Closing without applying resets the form.
 */
if (!customElements.get('swist-filters')) {
  customElements.define(
    'swist-filters',
    class SwistFilters extends HTMLElement {
      #abort = new AbortController();
      /** @type {HTMLElement | null} */
      #opener = null;
      #countTimer = 0;
      /** @type {AbortController | null} */
      #countRequest = null;

      connectedCallback() {
        const { signal } = this.#abort;
        this.panel = this.querySelector('[data-sfb-panel]');
        this.overlay = this.querySelector('[data-sfb-overlay]');
        if (!(this.panel instanceof HTMLFormElement)) return;

        this.addEventListener('click', (event) => this.#onClick(event), { signal });
        this.panel.addEventListener('change', (event) => this.#onChange(event), { signal });
        this.panel.addEventListener('input', (event) => this.#onInput(event), { signal });
        this.panel.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
        this.panel.addEventListener('keydown', (event) => this.#onKeyDown(event), { signal });
        this.overlay?.addEventListener('click', () => this.close(), { signal });
        window.addEventListener(
          'resize',
          () => {
            if (!this.panel.hidden && this.panel.dataset.mode === 'single') this.#position();
          },
          { passive: true, signal }
        );
        this.#syncPriceChips();
      }

      disconnectedCallback() {
        this.#abort.abort();
        this.#abort = new AbortController();
        this.#countRequest?.abort();
        clearTimeout(this.#countTimer);
        document.documentElement.classList.remove('sfb-lock');
      }

      /**
       * Opens the panel for one filter group ("sort" or its index) or for all filters ("all").
       * @param {string} key
       * @param {HTMLElement} opener
       */
      open(key, opener) {
        const panel = this.panel;
        if (!panel) return;
        const single = key !== 'all';
        // Read the pill's box before any DOM write: reading it afterwards forces a full-page layout
        // in the middle of the tap (the scroll lock below re-lays out the whole document).
        const openerRect = single ? opener.getBoundingClientRect() : null;
        if (!panel.hidden) this.close(false);

        panel.dataset.mode = single ? 'single' : 'all';
        panel.dataset.group = key;

        let label = '';
        for (const group of panel.querySelectorAll('[data-sfb-group]')) {
          const current = group.getAttribute('data-sfb-group') === key;
          group.classList.toggle('is-current', current);
          if (current) label = group.getAttribute('data-label') ?? '';
        }

        const title = panel.querySelector('[data-sfb-panel-title]');
        if (title) title.textContent = single ? label : title.getAttribute('data-all-title') ?? '';

        // Sorting from its own pill applies immediately, so it has no footer
        const footer = panel.querySelector('[data-sfb-footer]');
        if (footer instanceof HTMLElement) footer.hidden = key === 'sort';

        this.#opener = opener;
        opener.classList.add('is-open');
        opener.setAttribute('aria-expanded', 'true');

        if (openerRect) this.#position(openerRect);
        panel.hidden = false;
        if (this.overlay) this.overlay.hidden = false;
        document.documentElement.classList.add('sfb-lock');

        // focus() needs fresh layout; on the next frame that layout is already done.
        requestAnimationFrame(() => {
          if (!panel.hidden) panel.querySelector('[data-sfb-close]')?.focus({ preventScroll: true });
        });
      }

      /** Closes the panel; unless applying, drops unapplied changes. */
      close(restoreFocus = true) {
        const panel = this.panel;
        if (!panel || panel.hidden) return;
        panel.hidden = true;
        if (this.overlay) {
          this.overlay.hidden = true;
          this.overlay.style.removeProperty('inset-block-start');
        }
        document.documentElement.classList.remove('sfb-lock');

        panel.reset();
        this.#syncPriceChips();
        this.#countRequest?.abort();
        clearTimeout(this.#countTimer);
        this.#setApplyCount(this.querySelector('[data-sfb-count]')?.getAttribute('data-sfb-count') ?? '');

        const opener = this.#opener;
        this.#opener = null;
        if (opener) {
          opener.classList.remove('is-open');
          opener.setAttribute('aria-expanded', 'false');
          if (restoreFocus) opener.focus({ preventScroll: true });
        }
      }

      /**
       * Places the single-filter popover under its pill.
       * @param {DOMRect} [rect] - The pill's box, when the caller already measured it
       */
      #position(rect) {
        const opener = this.#opener;
        const panel = this.panel;
        if (!opener || !panel) return;
        rect ??= opener.getBoundingClientRect();
        const top = Math.round(rect.bottom + 8);
        const width = panel.dataset.group === 'sort' ? 260 : 340;
        const left = Math.round(Math.min(Math.max(rect.left, 8), window.innerWidth - width - 8));
        panel.style.setProperty('--sfb-top', `${top}px`);
        panel.style.setProperty('--sfb-left', `${left}px`);
        // Keep the pill row visible above the dimmed page
        if (this.overlay) this.overlay.style.insetBlockStart = `${Math.round(rect.bottom + 4)}px`;
      }

      /**
       * Opens the header's category menu drawer the same way the bottom nav "Kategoriler" tab
       * does (its summary owns the open state). Returns false when there is no drawer.
       * @returns {boolean}
       */
      #openMenuDrawer() {
        const details = document.getElementById('Details-menu-drawer-container');
        const summary = details?.querySelector(':scope > summary');
        if (!(summary instanceof HTMLElement)) return false;
        if (!details.hasAttribute('open')) summary.click();
        return true;
      }

      #onClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const back = target.closest('[data-sfb-back], [data-sfb-menu]');
        if (back) {
          if (back.hasAttribute('data-sfb-menu') && this.#openMenuDrawer()) {
            event.preventDefault();
            return;
          }
          if (back.hasAttribute('data-sfb-back') && document.referrer.startsWith(location.origin) && history.length > 1) {
            event.preventDefault();
            history.back();
          }
          return;
        }

        const opener = target.closest('[data-sfb-open]');
        if (opener instanceof HTMLElement) {
          const key = opener.getAttribute('data-sfb-open') ?? 'all';
          if (this.#opener === opener && !this.panel?.hidden) this.close();
          else this.open(key, opener);
          return;
        }

        if (target.closest('[data-sfb-close]')) {
          this.close();
          return;
        }

        const accordion = target.closest('[data-sfb-acc]');
        if (accordion && this.panel?.dataset.mode === 'all') {
          const group = accordion.closest('[data-sfb-group]');
          const open = accordion.getAttribute('aria-expanded') !== 'true';
          accordion.setAttribute('aria-expanded', String(open));
          group?.setAttribute('data-open', String(open));
          return;
        }

        if (target.closest('[data-sfb-clear]')) {
          this.#clear();
          return;
        }

        const chip = target.closest('.sfb__pchip');
        if (chip instanceof HTMLElement) {
          const group = chip.closest('[data-sfb-group]');
          const min = group?.querySelector('[data-sfb-min]');
          const max = group?.querySelector('[data-sfb-max]');
          const isActive = chip.classList.contains('is-active');
          if (min instanceof HTMLInputElement) min.value = isActive ? '' : chip.dataset.min ?? '';
          if (max instanceof HTMLInputElement) max.value = isActive ? '' : chip.dataset.max ?? '';
          this.#syncPriceChips();
          this.#queueCount();
        }
      }

      #onChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name === 'sort_by' && this.panel?.dataset.group === 'sort') {
          // Arrow keys move through radios and fire change on each step: only a pointer choice
          // applies at once; keyboard users confirm with Enter or Space
          if (this.#arrowKeySort) {
            this.#arrowKeySort = false;
            return;
          }
          this.panel.requestSubmit();
          return;
        }
        this.#queueCount();
      }

      #onInput(event) {
        if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'number') return;
        this.#syncPriceChips();
        this.#queueCount();
      }

      #onSubmit(event) {
        event.preventDefault();
        location.assign(this.#targetUrl().toString());
      }

      #arrowKeySort = false;

      #onKeyDown(event) {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.name === 'sort_by' && this.panel?.dataset.group === 'sort') {
          if (event.key.startsWith('Arrow')) {
            this.#arrowKeySort = true;
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            target.checked = true;
            this.panel.requestSubmit();
            return;
          }
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab' || !this.panel) return;

        // Keep keyboard focus inside the open panel
        const focusable = [
          ...this.panel.querySelectorAll('button, input, a[href], [tabindex]:not([tabindex="-1"])'),
        ].filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /** Clears the visible group (single mode) or every filter (all mode); sorting is kept. */
      #clear() {
        const panel = this.panel;
        if (!panel) return;
        const scope =
          panel.dataset.mode === 'single'
            ? panel.querySelectorAll('[data-sfb-group].is-current')
            : panel.querySelectorAll('[data-sfb-group]:not([data-sfb-group="sort"])');
        for (const group of scope) {
          for (const input of group.querySelectorAll('input')) {
            if (input.type === 'checkbox') input.checked = false;
            else if (input.type === 'number') input.value = '';
          }
        }
        this.#syncPriceChips();
        this.#queueCount();
      }

      /** Builds the results URL from the form, leaving out empty values. */
      #targetUrl() {
        const panel = this.panel;
        const url = new URL(panel?.getAttribute('action') || location.pathname, location.origin);
        url.search = '';
        if (!panel) return url;
        for (const [name, value] of new FormData(panel)) {
          if (typeof value === 'string' && value.trim() !== '') url.searchParams.append(name, value.trim());
        }
        return url;
      }

      #queueCount() {
        clearTimeout(this.#countTimer);
        this.#countTimer = window.setTimeout(() => this.#fetchCount(), 300);
      }

      /** Asks the section how many products the pending choices match. */
      async #fetchCount() {
        this.#countRequest?.abort();
        const request = new AbortController();
        this.#countRequest = request;

        const url = this.#targetUrl();
        url.searchParams.set('section_id', this.dataset.sectionId ?? '');
        const apply = this.querySelector('[data-sfb-apply]');
        apply?.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(url, { signal: request.signal });
          if (!response.ok) throw new Error(String(response.status));
          const html = await response.text();
          const count = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-sfb-count]')
            ?.getAttribute('data-sfb-count');
          if (this.#countRequest === request) this.#setApplyCount(count ?? '');
        } catch (error) {
          if (request.signal.aborted) return;
          this.#setApplyCount('');
        } finally {
          if (this.#countRequest === request) apply?.removeAttribute('aria-busy');
        }
      }

      /** Shows "N ürünü göster" when a count is known, otherwise "Uygula". */
      #setApplyCount(count) {
        const apply = this.querySelector('[data-sfb-apply]');
        if (!apply) return;
        const showLabel = apply.getAttribute('data-show-label') ?? '';
        apply.textContent =
          count !== '' && showLabel ? showLabel.replace('[count]', count) : apply.getAttribute('data-apply-label') ?? '';
      }

      /** Highlights the quick price range that matches the min/max inputs. */
      #syncPriceChips() {
        for (const group of this.querySelectorAll('[data-sfb-group]')) {
          const min = group.querySelector('[data-sfb-min]');
          const max = group.querySelector('[data-sfb-max]');
          if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) continue;
          for (const chip of group.querySelectorAll('.sfb__pchip')) {
            if (!(chip instanceof HTMLElement)) continue;
            const match = (chip.dataset.min ?? '') === min.value && (chip.dataset.max ?? '') === max.value;
            chip.classList.toggle('is-active', match && (min.value !== '' || max.value !== ''));
          }
        }
      }
    }
  );
}
